import * as React from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

type Status = 'idle' | 'exiting' | 'entering';
type Direction = 'increment' | 'decrement' | null;

// https://github.com/pmndrs/react-spring/issues/664#issuecomment-585748356
// Globals doesn't seem to be an available export on react-spring@8.0.27
// so I am just rendering the <animated.div> only if animation is not disabled
let animationDisabled = false;
if (performance && performance.mark && performance.getEntries) {
  performance.mark('dummy_check');
  const entries = performance.getEntries();

  animationDisabled = entries && entries.length === 0;
}

const getTranslateValue = (status: Status, direction: Direction): number => {
  switch (status) {
    case 'idle':
      return 0;
    case 'exiting': {
      return direction === 'increment' ? -100 : 100;
    }
    case 'entering': {
      return direction === 'increment' ? 100 : -100;
    }
  }
};

interface Props {
  children: React.ReactNode;
  currentStepNum: number;
}

export const StepTransitionManager: React.FC<Props> = ({ children, currentStepNum }) => {
  /**
   * React throws away children the moment they change, but in the case of
   * this component, we need to preserve the children until the animation
   * has completed. The trick used by react-transition-group and other
   * solutions is to copy the children into state, and update it once the
   * transition has completed.
   *
   * Copying props to state is generally seen as an anti-pattern, but the
   * animation usecase is a well-known exception / the only practical way
   * to do it.
   */
  const [cachedChildren, setCachedChildren] = React.useState(children);
  const [cachedStepNum, setCachedStepNum] = React.useState(currentStepNum);
  const [status, setStatus] = React.useState<Status>('idle');
  const [direction, setDirection] = React.useState<Direction>(null);

  const handleRest = (): void => {
    if (status === 'exiting') {
      setStatus('entering');
      setCachedChildren(children);
    } else if (status === 'entering') {
      setStatus('idle');
    }
  };

  const springConfig =
    status === 'exiting'
      ? {
          tension: 2500,
          friction: 140,
          // Way looser precision means that the `onRest` handler is called
          // sooner. In combination with clamp, it means it gets triggered
          // right when the animation reaches near the end.
          precision: 30,
          clamp: true,
        }
      : {
          tension: 2500,
          friction: 300,
          precision: 0.1,
        };

  const spring = useSpring({
    x: getTranslateValue(status, direction),
    onRest: handleRest,
    immediate: status === 'entering',
    config: springConfig,
  });

  const hasMounted = React.useRef(false);

  React.useEffect(() => {
    // Ignore the initial render - we don't support "appear" animations.
    if (!hasMounted.current) {
      return;
    }

    setDirection(currentStepNum > cachedStepNum ? 'increment' : 'decrement');
    setCachedStepNum(currentStepNum);

    setStatus('exiting');
  }, [currentStepNum, cachedStepNum]);

  // If the children are updated while no transition is in progress, swap them
  // out. This is to prevent a bug where state changes in children aren't
  // reflected in the UI.
  React.useEffect(() => {
    if (status === 'idle') {
      setCachedChildren(children);
    }
  }, [status, children]);

  React.useEffect(() => {
    hasMounted.current = true;
  }, []);

  return (
    <Wrapper>
      {animationDisabled ? (
        children
      ) : (
        <animated.div
          style={{
            transform: spring.x.to((x) => `translateX(${x}%)`),
          }}
        >
          {cachedChildren}
        </animated.div>
      )}
    </Wrapper>
  );
};
